/**
 * A round on one device, by taking turns (`docs/decisions/0059`): two to four
 * players each play one normal solo run, one after another, on the same seed,
 * and the round ranks them by the mode's own measure.
 *
 * Pure: no rune, no storage, no clock, no network. The screen
 * (`screens/LocalRoundScreen.svelte`) holds a `LocalRound` and replaces it
 * with what these functions return; the shell draws the parts (`HandOff`,
 * `Standings`, `PlayerNames`) and this file decides what they say.
 *
 * **Kept apart from every online record.** Nothing here (or in the screen)
 * reads or writes a personal best, the solo statistics, the daily or a
 * record; a finished round becomes a `LocalSession` for the device's own
 * history (`local/history-io.ts`) and nothing else.
 */
import { competitionRanks } from '@artibens/app-shell';
import type { StandingsRow } from '@artibens/app-shell';
import { encodeModeConfig, formatClock } from '@interlock/modes';
import type { ModeConfig, ModeOutcome } from '@interlock/modes';
import {
  LOCAL_NAME_MAX_LENGTH,
  LOCAL_PLAYERS_MAX,
  LOCAL_PLAYERS_MIN,
  LOCAL_TURN_LINES_MAX,
  LOCAL_TURN_TIME_MAX_MS,
} from '@interlock/protocol';
import type { LocalSession, LocalTurnOutcome } from '@interlock/protocol';

/** How one player's turn went: the facts a ranking needs, nothing more. */
export interface TurnResult {
  readonly outcome: LocalTurnOutcome;
  readonly lines: number;
  readonly timeMs: number;
}

/** One round: who plays, in turn order, on which seed, and how the turns played so far went. */
export interface LocalRound {
  readonly config: ModeConfig;
  /** The names, resolved (`resolvePlayerNames`), in turn order. */
  readonly players: readonly string[];
  readonly seed: number;
  /** 1 for the first round of a sitting; Play again counts up. */
  readonly number: number;
  /** One per turn played, in turn order. */
  readonly results: readonly TurnResult[];
}

/* --- the seed rule (PROVISIONAL) ------------------------------------------ */

/**
 * **Provisional** (the owner's decision of 2026-10-05, to be confirmed after
 * play): every player of a round plays the same seed, so the round is fair
 * (the same pieces in the same order for everyone), and each new round
 * ("Play again") draws a fresh one, so a second round cannot be memorised.
 * The rule is this constant and the two functions under it, and nothing else
 * decides a seed: changing it is changing these.
 */
export const LOCAL_SEED_RULE = 'same-seed-per-round-fresh-each-round' as const;

/** The seed a turn plays: under `LOCAL_SEED_RULE`, the round's own, whoever's turn it is. */
export function seedForTurn(round: LocalRound, turn: number): number {
  void turn;
  return round.seed;
}

/**
 * A seed for a new round: an unsigned 32-bit number from `draw`, never the
 * previous round's (a fresh round must not be a repeat, however unlikely a
 * repeat is). `draw` is the caller's source of randomness, so this stays
 * pure and testable.
 */
export function freshRoundSeed(previous: number | null, draw: () => number): number {
  for (let attempt = 0; attempt < 8; attempt++) {
    const seed = draw() >>> 0;
    if (seed !== previous) return seed;
  }
  // `draw` kept answering the same number: step off it rather than repeat.
  return ((previous ?? 0) + 1) >>> 0;
}

/* --- the round ------------------------------------------------------------- */

/**
 * The turn order of a round: the seats as the setup lists them, every round
 * (the first seat starts). A rule of its own so a later order (rotating who
 * starts) is a change here and nowhere else.
 */
export function turnOrder(players: readonly string[]): readonly string[] {
  return [...players];
}

/** A new round. Two to four players; anything else is a programming error. */
export function startRound(
  players: readonly string[],
  config: ModeConfig,
  seed: number,
  number = 1,
): LocalRound {
  if (players.length < LOCAL_PLAYERS_MIN || players.length > LOCAL_PLAYERS_MAX) {
    throw new RangeError(
      `a round takes ${String(LOCAL_PLAYERS_MIN)} to ${String(LOCAL_PLAYERS_MAX)} players, not ${String(players.length)}`,
    );
  }
  return { config, players: turnOrder(players), seed: seed >>> 0, number, results: [] };
}

/** The same players and the same mode, a fresh seed, the next number: "Play again". */
export function nextRound(round: LocalRound, seed: number): LocalRound {
  return startRound(round.players, round.config, seed, round.number + 1);
}

/** Whose turn it is (0-based), or `players.length` once every player has played. */
export function turnIndex(round: LocalRound): number {
  return round.results.length;
}

export function isRoundOver(round: LocalRound): boolean {
  return round.results.length >= round.players.length;
}

/** The player whose turn it is, or `null` once the round is over. */
export function currentPlayer(round: LocalRound): string | null {
  return round.players[round.results.length] ?? null;
}

/** The player after the current one, or `null` when the current one is the last. */
export function playerAfterCurrent(round: LocalRound): string | null {
  return round.players[round.results.length + 1] ?? null;
}

/** The current player's turn is over, however it ended. */
export function recordTurn(round: LocalRound, result: TurnResult): LocalRound {
  if (isRoundOver(round)) throw new Error('every player of this round has played');
  return { ...round, results: [...round.results, result] };
}

/**
 * How a run that reached its end counts in a round. A Sprint that topped out
 * (`lose`) did not reach its goal or its clock: `topped-out`. A Survival run
 * always ends by being buried, which is its finish, not a failure.
 */
export function turnOutcomeFor(config: ModeConfig, outcome: ModeOutcome): LocalTurnOutcome {
  if (config.modeId === 'survival') return 'finished';
  return outcome.result === 'lose' ? 'topped-out' : 'finished';
}

/* --- ranking -------------------------------------------------------------- */

/** What a round is decided by, per mode: the mode's own measure. */
export interface RankingRule {
  readonly measure: 'timeMs' | 'lines';
  readonly better: 'lower' | 'higher';
}

/**
 * - **Sprint to a line goal**: the time to reach it, lower first.
 * - **Sprint against the clock**: the lines cleared, higher first.
 * - **Survival**: the time survived, higher first.
 */
export function rankingRule(config: ModeConfig): RankingRule {
  if (config.modeId === 'survival') return { measure: 'timeMs', better: 'higher' };
  if (config.goal?.kind === 'time') return { measure: 'lines', better: 'higher' };
  return { measure: 'timeMs', better: 'lower' };
}

/** Wider than any time or line count a turn can report; keeps the tiers apart. */
const SPAN = 2 ** 40;

const TIER: Readonly<Record<LocalTurnOutcome, number>> = {
  finished: 2,
  'topped-out': 1,
  quit: 0,
};

/**
 * Competition ranks ("1224", the shell's `competitionRanks`) for `results`,
 * in their own order. Three tiers, each wholly above the next:
 *
 * 1. **Finished** runs, by the mode's measure (`rankingRule`);
 * 2. **Topped-out** Sprints (a top-out before the goal or the clock), below
 *    every finisher, by the lines they cleared, more first;
 * 3. **Quit** turns, below everyone who played on, all tied.
 *
 * Equal figures share a rank, and a time is compared at the precision it is
 * shown (a tenth of a second, `formatClock`): two times that read the same
 * are a tie, never a win decided by milliseconds nobody can see.
 */
export function rankTurns(config: ModeConfig, results: readonly TurnResult[]): number[] {
  const rule = rankingRule(config);
  const scores = results.map((result) => {
    const tier = TIER[result.outcome];
    let within = 0;
    if (result.outcome === 'finished') {
      const raw = rule.measure === 'timeMs' ? shownTenths(result.timeMs) : result.lines;
      const value = Math.min(Math.max(0, raw), SPAN - 1);
      within = rule.better === 'higher' ? value : SPAN - 1 - value;
    } else if (result.outcome === 'topped-out') {
      within = Math.min(Math.max(0, result.lines), SPAN - 1);
    }
    return tier * SPAN + within;
  });
  return competitionRanks(scores, 'higher');
}

/** A time in the tenths of a second `formatClock` writes it in. */
function shownTenths(ms: number): number {
  return Math.round(Math.max(0, ms) / 100);
}

/** The figure a row shows: the mode's measure for a finisher, the lines for a top-out, a dash for a quit. */
export function figureFor(config: ModeConfig, result: TurnResult): string {
  if (result.outcome === 'quit') return '—';
  const rule = rankingRule(config);
  if (result.outcome === 'topped-out') return linesText(result.lines);
  return rule.measure === 'timeMs' ? formatClock(result.timeMs) : linesText(result.lines);
}

/** The quieter line under a row's name: how the turn ended. */
export function detailFor(config: ModeConfig, result: TurnResult): string {
  switch (result.outcome) {
    case 'quit':
      return 'Quit';
    case 'topped-out':
      return `Topped out at ${formatClock(result.timeMs)}`;
    case 'finished':
      if (config.modeId === 'survival') return linesText(result.lines);
      if (config.goal?.kind === 'time') return "Time's up";
      return linesText(result.lines);
  }
}

function linesText(lines: number): string {
  return `${String(lines)} ${lines === 1 ? 'line' : 'lines'}`;
}

/** One played turn, ranked among the turns played so far. */
export interface RankedTurn {
  readonly seat: number;
  readonly name: string;
  readonly rank: number;
  readonly result: TurnResult;
  /** Shares rank 1 and played on: a quit never wins, even when everybody quit. */
  readonly winner: boolean;
}

/** The turns played so far, ranked; in seat order (the shell draws `Standings` in rank order). */
export function rankedTurns(round: LocalRound): RankedTurn[] {
  const ranks = rankTurns(round.config, round.results);
  return round.results.map((result, seat) => {
    const rank = ranks[seat] ?? round.results.length;
    return {
      seat,
      name: round.players[seat] ?? '',
      rank,
      result,
      winner: rank === 1 && result.outcome !== 'quit',
    };
  });
}

/**
 * The rows for `Standings`. Mid-round (the hand-off) only the players who
 * have played are listed, and nobody is marked the winner yet; once the round
 * is over every player is listed and the winners are marked.
 */
export function standingsRows(round: LocalRound): StandingsRow[] {
  const over = isRoundOver(round);
  return rankedTurns(round).map((turn) => ({
    id: `seat-${String(turn.seat + 1)}`,
    rank: turn.rank,
    name: turn.name,
    value: figureFor(round.config, turn.result),
    detail: detailFor(round.config, turn.result),
    winner: over && turn.winner,
    testid: `local-standing-${String(turn.seat + 1)}`,
  }));
}

/** The results' headline: who won, who tied, or that nobody played on. */
export function headlineFor(round: LocalRound): string {
  const winners = rankedTurns(round)
    .filter((turn) => turn.winner)
    .map((turn) => turn.name);
  if (winners.length === 0) return 'Nobody played on';
  if (winners.length === 1) return `${winners[0] ?? ''} wins`;
  return `${listNames(winners)} tie`;
}

/** "Noor and Sam", "Noor, Sam and Robin". */
export function listNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1) ?? ''}`;
}

/** The finished round as the device's history keeps it: every seat, in turn order, with its rank. */
export function toLocalSession(round: LocalRound, id: string, playedAtMs: number): LocalSession {
  if (!isRoundOver(round)) throw new Error('only a finished round is kept');
  return {
    id,
    playedAtMs,
    modeKey: encodeModeConfig(round.config),
    players: rankedTurns(round).map((turn) => ({
      name: storableName(turn.name, turn.seat),
      rank: turn.rank,
      outcome: turn.result.outcome,
      lines: wholeWithin(turn.result.lines, LOCAL_TURN_LINES_MAX),
      timeMs: wholeWithin(turn.result.timeMs, LOCAL_TURN_TIME_MAX_MS),
    })),
  };
}

/**
 * A name as the history's validator takes it (`@interlock/protocol`): at
 * most sixteen characters, trimmed, no invisible character. Names come from
 * `resolvePlayerNames` and the 16-character fields, so this changes nothing
 * in practice; it keeps a prefilled long account name from making the round
 * one the store would refuse.
 */
function storableName(name: string, seat: number): string {
  const kept = Array.from(name.replace(/[\p{Cc}\p{Cf}]/gu, ''))
    .slice(0, LOCAL_NAME_MAX_LENGTH)
    .join('')
    .trim();
  return kept.length > 0 ? kept : `Player ${String(seat + 1)}`;
}

function wholeWithin(value: number, max: number): number {
  return Math.min(max, Math.max(0, Math.round(value)));
}
